// Library Methods
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import triggerButtonImg from "src/assets/images/operating-manual/instructions-for-use/trigger-button.png";
import recenterButtonImg from "src/assets/images/operating-manual/instructions-for-use/recenter-button.png";
import headsetPosition1Img from "src/assets/images/operating-manual/instructions-for-use/headset-position-1.png";
import headsetPosition2Img from "src/assets/images/operating-manual/instructions-for-use/headset-position-2.png";
import patientAction1Img from "src/assets/images/operating-manual/instructions-for-use/patient-action-1.png";
import patientAction2Img from "src/assets/images/operating-manual/instructions-for-use/patient-action-2.png";
import patientView1Img from "src/assets/images/operating-manual/instructions-for-use/patient-view-1.png";
import patientView2Img from "src/assets/images/operating-manual/instructions-for-use/patient-view-2.png";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

const LaunchAnExam = () => {
  const { t } = useTranslation("operating-manual");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Typography>
        {t(
          "To see what the exam looks like from the patient perspective, see "
        )}
        <Link
          to="/knowledge-hub/operating-manual/appendix-b"
          target="_blank"
          style={{ color: "#0066CC", textDecoration: "underline" }}
        >
          {t("Appendix B - Patient View")}.
        </Link>
        {t(
          " Ensure the exam is linked by checking the device card in the devices section. It will be grey, and the patient name will appear, like so:"
        )}
      </Typography>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: "20px",
            }}
          >
            <img
              src={triggerButtonImg}
              width="100%"
              alt="trigger-button"
              style={{
                objectFit: "contain",
                height: "100%",
                maxHeight: "150px",
              }}
            />
          </Box>
          <Typography>
            <span style={{ fontWeight: 600 }}>1.</span>{" "}
            {t(
              "Show the patient the controller. Instruct them that they will only need to use the trigger button during the test."
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: "20px",
            }}
          >
            <img
              src={headsetPosition1Img}
              width="100%"
              alt="headset-position-1"
              style={{
                objectFit: "contain",
                height: "100%",
                maxHeight: "150px",
              }}
            />
          </Box>
          <Typography>
            <span style={{ fontWeight: 600 }}>2.</span>{" "}
            {t(
              "Loosen the strap by twisting the dial on the back counter- clockwise. Lift the arms and assist the patient in putting on the mask. Ask them to adjust the mask up/down until they can read the letters."
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: "20px",
            }}
          >
            <img
              src={headsetPosition2Img}
              width="100%"
              alt="headset-position-2"
              style={{
                objectFit: "contain",
                height: "100%",
                maxHeight: "150px",
              }}
            />
          </Box>
          <Typography>
            <span style={{ fontWeight: 600 }}>3.</span>{" "}
            {t(
              "Rotate the strap down over the head. Instruct the patient to twist the dial on the back of the device clockwise to tighten until they are comfortable."
            )}
          </Typography>
        </Grid>
      </Grid>

      <Typography>
        <span style={{ fontWeight: 600 }}>4.</span>{" "}
        {t("Press the recenter button")}
        <Box
          component="img"
          src={recenterButtonImg}
          alt="recenter-button"
          sx={{ mx: "8px" }}
          style={{
            display: "inline",
            width: "45px",
            height: `${UI_SIZES.iconButtonSize}px`,
            verticalAlign: "middle",
          }}
        />
        {t(
          "on the controller and hand it to the patient. This will ensure the scene is centered for the patient. This can also be done remotely in the live monitoring feature, see below for more details."
        )}
      </Typography>

      <Box
        sx={{
          py: "20px",
          borderRadius: "8px",
          backgroundColor: "#5B6D7A30",
          display: { xs: "none", sm: "flex" },
        }}
      >
        <Grid container spacing="20px">
          <Grid item xs={6} />
          <Grid item xs={3}>
            <Typography textAlign="center">{t("Patient Action")}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography textAlign="center">{t("Patient View")}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            <span style={{ fontWeight: 600 }}>5.</span>{" "}
            {t(
              "Instruct the patient to press the trigger button to load the exam."
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <img
            src={patientAction1Img}
            width="100%"
            alt="patient-action-1"
            style={{ width: "100%", minHeight: "150px", objectFit: "cover" }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <img
            src={patientView1Img}
            width="100%"
            alt="patient-view-1"
            style={{ width: "100%", minHeight: "150px", objectFit: "cover" }}
          />
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            <span style={{ fontWeight: 600 }}>6.</span>{" "}
            {t(
              "Instruct the patient to hold the trigger for 3 seconds to launch the exam."
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <img
            src={patientAction2Img}
            width="100%"
            alt="patient-action-2"
            style={{ width: "100%", minHeight: "150px", objectFit: "cover" }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <img
            src={patientView2Img}
            width="100%"
            alt="patient-view-2"
            style={{ width: "100%", minHeight: "150px", objectFit: "cover" }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LaunchAnExam;
